'use client';

import { jobdetailintro } from '@/static/job_detail_intro';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';

interface JobDetailVersion {
  title: string;
  titleColor?: string;
  description: string;
  bullets: {
    title: string;
    description: string;
    image: string;
  }[];
}

// Tách phần content thành component riêng để có thể sử dụng key
const JobDetailContent: React.FC<{
  version: JobDetailVersion;
}> = ({ version }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []); // Chỉ chạy một lần khi mount

  return (
    <section
      ref={sectionRef}
      id="job-detail"
      className="max-w-8xl mx-auto overflow-hidden py-16 md:px-[50px]"
    >
      <div className="max-w-8xl mx-auto px-5 md:px-0">
        <h2 className="mb-16 text-3xl font-bold lg:max-w-4xl lg:text-5xl lg:leading-tight">
          {version.title}
          <br />
          <span className="text-[#9447FF]">{version.titleColor}</span>
        </h2>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          {version.bullets.map((bullet, index) => (
            <div
              key={index}
              className={`flex flex-col items-center gap-4 text-center transition-all duration-1000 ease-out md:flex-row md:items-center md:text-left lg:flex-col lg:gap-0 lg:text-center ${
                isVisible
                  ? 'translate-x-0 opacity-100'
                  : index === 0
                    ? '-translate-x-full opacity-0'
                    : 'translate-x-full opacity-0'
              }`}
              style={{
                transitionDelay: `${index * 200}ms`,
              }}
            >
              <div className="relative w-full md:flex-1 lg:mb-4 lg:flex-shrink-0">
                <Image
                  src={bullet.image}
                  alt={bullet.title}
                  width={600}
                  height={500}
                  className="h-auto w-full rounded-[48px] object-contain"
                  loading="lazy"
                  sizes="(min-width: 768px) 50vw, 100vw"
                />
              </div>
              <div className="w-full text-center md:flex-1 lg:flex-none">
                <h3 className="mb-2 text-lg font-semibold text-gray-800 md:text-xl lg:text-2xl">
                  {bullet.title}
                </h3>
                <p className="text-sm leading-[1.5] text-gray-600 md:text-base">
                  {bullet.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const JobDetail: React.FC = () => {
  const searchParams = useSearchParams();
  const urlVersion = searchParams?.get('version')?.toUpperCase() || 'A';
  const variant = urlVersion === 'A' || urlVersion === 'B' ? urlVersion : 'A';

  // Lấy version tương ứng từ dữ liệu
  const versionKey = `version${variant}` as 'versionA' | 'versionB';
  const version = jobdetailintro[0][versionKey];

  if (!version) {
    console.error(`Version ${variant} not found in jobdetailintro data`);

    return null;
  }

  // Sử dụng key để force re-render khi variant thay đổi
  return <JobDetailContent key={variant} version={version} />;
};

export default JobDetail;
